import { Card, CardBody, Button, Chip } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useAppApi } from "src/appApi";

const AuthScreen = () => {
	const appApi = useAppApi();

	const mainServices = [
		{
			icon: "lucide:file-plus",
			title: "הנפקת רישיון חדש / חידוש רישיון",
			description: "הגשת בקשה לרישיון קנאביס רפואי חדש או חידוש רישיון קיים",
			features: ["הגשת בקשה מקוונת", "ליווי רפואי מלא", "מעקב אחר סטטוס הבקשה"],
			color: "primary",
			action: () => appApi.navigate("/new_request"),
		},
		{
			icon: "lucide:lightbulb",
			title: "בקשה להמלצה רפואית בלבד",
			description: "הגשת בקשה להמלצה רפואית ללא צורך ברישיון מלא",
			features: ["תהליך מהיר ופשוט", "אמייל אינו חובה", "עלות נמוכה יותר"],
			color: "secondary",
			action: () => appApi.navigate("/new_recommendation"),
		},
		{
			icon: "lucide:scale",
			title: "מיצוי זכויות עם עורך דין",
			description: "ייצוג משפטי ומיצוי זכויות במקרים הקשורים לקנאביס רפואי",
			features: ["ייעוץ משפטי מקצועי", "ייצוג בבתי משפט", "מיצוי זכויות מלא"],
			color: "success",
			action: () => window.open("https://wa.me/972545344991", "_blank"),
		},
		{
			icon: "lucide:store",
			title: "בית מרקחת",
			description: "רכישת מוצרי קנאביס רפואי איכותיים מבית המרקחת שלנו",
			features: ["מוצרים מאושרים", "איכות גבוהה", "משלוח מהיר ובטוח"],
			color: "secondary",
			action: () => appApi.navigate("/pharmacy"),
		},
		{
			icon: "lucide:shopping-bag",
			title: "עולם האקססוריז",
			description: "מגוון רחב של אקססוריז ועזרים לשימוש בקנאביס רפואי",
			features: ["ציוד איכותי", "מחירים תחרותיים", "ייעוץ מקצועי"],
			color: "warning",
			comingSoon: true,
		},
	];

	return (
		<div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 p-4 md:p-6">
			<div className="max-w-6xl mx-auto">
				{/* Hero Section */}
				<Card className="mb-8 bg-gradient-to-r from-primary-500 to-primary-600 text-white">
					<CardBody className="text-center py-12">
						<div className="mb-6">
							<Icon icon="lucide:leaf" className="h-16 w-16 mx-auto mb-4" />
							<h1 className="text-4xl md:text-5xl font-bold mb-4">ברוכים הבאים ל-CANA</h1>
							<p className="text-xl md:text-2xl opacity-90">
								הפלטפורמה המובילה לרישיון קנאביס רפואי בישראל
							</p>
						</div>
						<div className="flex flex-col sm:flex-row gap-4 justify-center">
							<Button
								color="secondary"
								size="lg"
								onPress={() => appApi.navigate("/new_request")}
								startContent={<Icon icon="lucide:file-text" />}
							>
								בקשה לרישיון מלא
							</Button>
							<Button
								color="warning"
								size="lg"
								onPress={() => appApi.navigate("/new_recommendation")}
								startContent={<Icon icon="lucide:lightbulb" />}
							>
								בקשה להמלצה בלבד
							</Button>
							<Button
								variant="bordered"
								size="lg"
								onPress={() => appApi.navigate("/my_profile")}
								startContent={<Icon icon="lucide:user" />}
								className="border-white text-white hover:bg-white hover:text-primary-600"
							>
								הפרופיל שלי
							</Button>
						</div>
					</CardBody>
				</Card>

				{/* Main Services Section */}
				<div className="mb-12">
					<h2 className="text-3xl font-bold text-center mb-8">השירותים שלנו</h2>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-8">
						{mainServices.map((service, index) => (
							<Card
								key={index}
								className="h-full relative overflow-hidden hover:shadow-lg transition-shadow"
							>
								{service.comingSoon && (
									<div className="absolute top-4 right-4 z-10">
										<Chip color="warning" variant="flat" size="sm">
											בקרוב
										</Chip>
									</div>
								)}
								<CardBody className="p-6">
									<div className="flex items-start gap-4 mb-4">
										<div
											className={
												service.color === "primary"
													? "bg-primary-100 p-3 rounded-full flex-shrink-0"
													: service.color === "success"
													? "bg-success-100 p-3 rounded-full flex-shrink-0"
													: service.color === "secondary"
													? "bg-secondary-100 p-3 rounded-full flex-shrink-0"
													: service.color === "warning"
													? "bg-warning-100 p-3 rounded-full flex-shrink-0"
													: "bg-danger-100 p-3 rounded-full flex-shrink-0"
											}
										>
											<Icon
												icon={service.icon}
												className={
													service.color === "primary"
														? "h-8 w-8 text-primary-600"
														: service.color === "success"
														? "h-8 w-8 text-success-600"
														: service.color === "secondary"
														? "h-8 w-8 text-secondary-600"
														: service.color === "warning"
														? "h-8 w-8 text-warning-600"
														: "h-8 w-8 text-danger-600"
												}
											/>
										</div>
										<div className="flex-1">
											<h3 className="text-xl font-bold mb-2">{service.title}</h3>
											<p className="text-default-600 mb-4">{service.description}</p>
										</div>
									</div>

									<div className="mb-6">
										<ul className="space-y-2">
											{service.features.map((feature, idx) => (
												<li key={idx} className="flex items-center gap-2 text-sm">
													<Icon
														icon="lucide:check"
														className="h-4 w-4 text-success-500 flex-shrink-0"
													/>
													<span>{feature}</span>
												</li>
											))}
										</ul>
									</div>

									{service.action && !service.comingSoon && (
										<Button
											color={
												service.color as
													| "primary"
													| "success"
													| "secondary"
													| "warning"
													| "danger"
											}
											variant="flat"
											onPress={service.action}
											className="w-full"
											startContent={<Icon icon="lucide:arrow-left" />}
										>
											התחל עכשיו
										</Button>
									)}

									{service.comingSoon && (
										<Button
											color={
												service.color as
													| "primary"
													| "success"
													| "secondary"
													| "warning"
													| "danger"
											}
											variant="flat"
											disabled
											className="w-full"
										>
											בקרוב
										</Button>
									)}
								</CardBody>
							</Card>
						))}
					</div>
				</div>

				{/* Contact Section */}
				<Card className="bg-default-50">
					<CardBody className="text-center py-8">
						<h2 className="text-2xl font-bold mb-4">צריכים עזרה?</h2>
						<p className="text-default-600 mb-6">הצוות שלנו זמין לעזור לך בכל שאלה או בעיה</p>
						<div className="flex flex-col sm:flex-row gap-4 justify-center">
							<Button
								color="primary"
								variant="flat"
								startContent={<Icon icon="lucide:mail" />}
							>
								צור קשר
							</Button>
							<Button
								color="secondary"
								variant="flat"
								startContent={<Icon icon="lucide:phone" />}
							>
								התקשר אלינו
							</Button>
						</div>
					</CardBody>
				</Card>
			</div>
		</div>
	);
};

export default AuthScreen;
